import { FC, useMemo } from 'react';

import 'views/design-system/tokens.scss';
import './passkey-setup.scss';

/**
 * The in-page state a "Device Biometric" click lands on when a passkey
 * sign-in is NOT the right next step. Three situations, one card:
 *
 * - `none`      — the account has no passkey at all (server answered
 *                 "No device registered"). `get()` was never called.
 * - `elsewhere` — the account HAS passkeys, but none of them were created in
 *                 this browser (see `hasLocalCredential`). `get()` was NOT
 *                 called: with a non-matching allow-list the browser raises
 *                 its native "no matching passkeys" sheet, which nothing on
 *                 the page can dismiss or explain. The card offers to set one
 *                 up here, to sign in another way, or — because a passkey
 *                 synced in from another Apple device is invisible to our
 *                 local record — to try the passkey anyway.
 * - `failed`    — `get()` (or `create()`) rejected. `NotAllowedError` is what
 *                 a cancel, a timeout and a "no credential" all throw, so the
 *                 copy blames none of them and offers every way forward.
 *
 * Always in-page, never a modal: the person has just closed one dialog they
 * could not act on, and the recovery must be somewhere they can read and
 * choose from without another overlay.
 *
 * Always ADDITIVE: it is inserted under the Device Biometric row and every
 * method the screen listed before it — and the screen's Back button — stays
 * in place. "Dismiss" only removes the card.
 *
 * Styling is `.sv2-scope` + tokens so it follows the design of record and
 * both themes; the login shell around it is still on the old styling and does
 * not claim dark mode yet, which is why this card must also read well on the
 * light shell — it does, because both palettes are neutral greys.
 */

export type PasskeySetupKind = 'none' | 'elsewhere' | 'failed';
export type PasskeyPhase = 'get' | 'create';
export type PasskeyBusy = 'setup' | 'approval' | 'passkey' | null;

export interface IPasskeySetupState {
	kind: PasskeySetupKind;
	/** how many passkeys the server holds for the account (kind `elsewhere`) */
	credentialCount?: number;
	/** which ceremony failed (kind `failed`) */
	phase?: PasskeyPhase;
	/** the DOMException name, shown small for support — never as the message */
	errorName?: string;
}

interface IPasskeySetup {
	state: IPasskeySetupState;
	/** the email or phone typed at sign-in */
	identity: string;
	/** 'email / mobile' or 'mobile' — mirrors the approval row's own label */
	approvalLabel: string;
	busy: PasskeyBusy;
	onSetUp: () => void;
	onUseApproval: () => void;
	onTryPasskey: () => void;
	onBack: () => void;
}

const COPY = {
	none: {
		title: 'No passkey on this account yet',
		body:
			'A passkey signs you in with Touch ID, Face ID or Windows Hello instead of a link. Set one up now, or continue with an approval link.',
		primary: 'Set up a passkey',
	},
	elsewhere: {
		title: 'Your passkey is not on this browser',
		body: '',
		primary: 'Set up a passkey on this browser',
	},
	failed: {
		title: 'That passkey did not go through',
		body:
			'The request was cancelled, timed out, or this browser could not find a matching passkey. Nothing is wrong with your account — pick another way in, or try again.',
		primary: 'Set up a passkey on this browser',
	},
} as const;

export const PasskeySetup: FC<IPasskeySetup> = ({
	state,
	identity,
	approvalLabel,
	busy,
	onSetUp,
	onUseApproval,
	onTryPasskey,
	onBack,
}) => {
	const { kind, credentialCount = 0, phase = 'get', errorName } = state;

	const title = useMemo(() => {
		if (kind === 'failed' && phase === 'create') {
			return 'Creating the passkey did not finish';
		}
		return COPY[kind].title;
	}, [kind, phase]);

	const body = useMemo(() => {
		if (kind === 'elsewhere') {
			const n = credentialCount;
			const noun = n === 1 ? 'passkey' : 'passkeys';
			return `This account has ${n || 'a'} ${noun}, but none of them were created in this browser — it most likely lives on another device. Add one for this browser, or sign in another way.`;
		}
		if (kind === 'failed' && phase === 'create') {
			return 'The passkey prompt was closed before it finished, so nothing was saved. Nothing is wrong with your account — you can try again, or continue with an approval link.';
		}
		return COPY[kind].body;
	}, [kind, phase, credentialCount]);

	const anyBusy = busy !== null;
	const showSteps = kind !== 'failed';
	const showTry = kind === 'elsewhere' || kind === 'failed';
	const primaryIsApproval = kind === 'failed';

	const setUpButton = (
		<button
			type="button"
			className={`pk-btn ${primaryIsApproval ? '' : 'pri'}`}
			onClick={onSetUp}
			disabled={anyBusy}
			data-testid="passkey-setup"
		>
			<i className="ri-fingerprint-line" aria-hidden="true" />
			{busy === 'setup' ? 'Sending approval link…' : COPY[kind].primary}
		</button>
	);

	const approvalButton = (
		<button
			type="button"
			className={`pk-btn ${primaryIsApproval ? 'pri' : ''}`}
			onClick={onUseApproval}
			disabled={anyBusy}
			data-testid="passkey-use-approval"
		>
			<i className="ri-smartphone-line" aria-hidden="true" />
			{busy === 'approval'
				? 'Sending link…'
				: `Use approval via ${approvalLabel} instead`}
		</button>
	);

	return (
		// `.sv2-scope` for the tokens only — NOT `.sv2`, which is the deck shell
		// (min-height: 100vh, page padding, page background) and would paint a
		// full-height slab into the login card.
		<div className="sv2-scope passkey-setup" data-kind={kind}>
			<div className="pk-card" role="status" aria-live="polite">
				<div className="pk-head">
					<div className="pk-icon" aria-hidden="true">
						<i
							className={
								kind === 'failed' ? 'ri-error-warning-line' : 'ri-key-2-line'
							}
						/>
					</div>
					<div className="pk-head-text">
						<div className="pk-title">{title}</div>
						{identity && <div className="pk-sub">Signing in as {identity}</div>}
					</div>
				</div>

				<p className="pk-body">{body}</p>

				{showSteps && (
					<ol className="pk-steps">
						<li>
							<span className="pk-step-n">1</span>
							<span>
								We send an approval link to your {approvalLabel} — it proves it
								is you before a new browser is trusted.
							</span>
						</li>
						<li>
							<span className="pk-step-n">2</span>
							<span>
								Open the link. This browser then asks you to create the passkey
								with Touch ID, Face ID or Windows Hello.
							</span>
						</li>
						<li>
							<span className="pk-step-n">3</span>
							<span>You are signed in, and next time it is one tap.</span>
						</li>
					</ol>
				)}

				<div className="pk-actions">
					{primaryIsApproval ? (
						<>
							{approvalButton}
							{setUpButton}
						</>
					) : (
						<>
							{setUpButton}
							{approvalButton}
						</>
					)}
				</div>

				<div className="pk-links">
					{showTry && (
						<button
							type="button"
							className="pk-link"
							onClick={onTryPasskey}
							disabled={anyBusy}
							data-testid="passkey-try"
						>
							{busy === 'passkey'
								? 'Waiting for your passkey…'
								: kind === 'failed'
								? 'Try the passkey again'
								: 'I do have a passkey here — try it'}
						</button>
					)}
					<button
						type="button"
						className="pk-link quiet"
						onClick={onBack}
						disabled={anyBusy}
						data-testid="passkey-back"
					>
						Dismiss
					</button>
				</div>

				{kind === 'failed' && errorName && (
					<div className="pk-note">Browser reported: {errorName}</div>
				)}
			</div>
		</div>
	);
};
